import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Package, ArrowRight, Ban, Sparkles, AlertTriangle } from "lucide-react";
import { api, apiError, money } from "../api";
import { PageLoader, StatusBadge, Spinner, Toast, useToast } from "../components/ui";

const SCAN_POLL_INTERVAL_MS = 2500;

export default function ScanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, show, close } = useToast();
  const [scan, setScan] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/scans/${id}`);
      setScan(data.scan);
      return data.scan.status;
    } catch (e) {
      show(apiError(e), "error");
      return "failed";
    }
  }, [id, show]);

  useEffect(() => {
    let active = true;
    const tick = async () => {
      const status = await load();
      if (!active) return;
      if (["pending", "running"].includes(status)) {
        timer.current = setTimeout(tick, SCAN_POLL_INTERVAL_MS);
      }
    };
    tick();
    return () => { active = false; if (timer.current) clearTimeout(timer.current); };
  }, [load]);

  const cancel = async () => {
    try {
      await api.post(`/scans/${id}/cancel`);
      show("Cancellation requested.");
      load();
    } catch (e) {
      show(apiError(e), "error");
    }
  };

  if (!scan) return <PageLoader label="Loading scan" />;

  const running = ["pending", "running"].includes(scan.status);
  const priceChanges = (scan.changes || []).filter((c) => c.type !== "no_rows");
  const noRows = (scan.changes || []).find((c) => c.type === "no_rows");

  return (
    <div className="fade-in pb-6">
      <Toast toast={toast} onClose={close} />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted mb-4" data-testid="scandetail-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl text-ink">{scan.profile_name}</h1>
          <p className="text-sm text-muted mt-1">Target {scan.target_date} · {scan.trigger} scan</p>
        </div>
        <StatusBadge status={scan.status} />
      </div>

      {running && (
        <div className="card p-4 mb-4 flex items-center gap-3" data-testid="scan-running">
          <Spinner className="w-5 h-5 text-teal" />
          <span className="text-sm text-muted flex-1">Scanning the Price Fixing Unit catalogue…</span>
          <button onClick={cancel} className="btn-ghost !min-h-[36px] !px-3 text-sm" data-testid="scan-cancel"><Ban className="w-4 h-4" /> Cancel</button>
        </div>
      )}

      {scan.status === "failed" && scan.error && (
        <div className="card p-4 mb-4 bg-rose-light text-rose text-sm" data-testid="scan-error">{scan.error}</div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {[
          { label: "Pages scanned", value: scan.pages_scanned, icon: FileText },
          { label: "Rows found", value: scan.rows_found, icon: FileText },
          { label: "Matched products", value: scan.matched_count, icon: Package },
          { label: "Price changes", value: scan.changes_count, icon: ArrowRight },
        ].map((s) => (
          <div key={s.label} className="card p-4" data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
            <s.icon className="w-4 h-4 text-teal mb-2" />
            <div className="font-display text-2xl text-ink">{s.value}</div>
            <div className="text-xs text-muted">{s.label}</div>
          </div>
        ))}
      </div>

      {noRows && (
        <div className="card p-4 mb-6 flex items-start gap-3 bg-amber-light" data-testid="scan-no-rows">
          <AlertTriangle className="w-5 h-5 text-amber shrink-0 mt-0.5" />
          <div className="text-sm text-amber">No prices were published for {scan.target_date} in this catalogue.</div>
        </div>
      )}

      <h2 className="font-display text-lg text-ink mb-3">Price changes</h2>
      {priceChanges.length === 0 ? (
        <p className="text-sm text-muted mb-6" data-testid="scan-no-changes">
          {running ? "Waiting for results…" : "No tracked prices changed for this date."}
        </p>
      ) : (
        <div className="card divide-y divide-stone-border mb-6" data-testid="scan-changes-list">
          {(priceChanges || []).map((c) => (
            <div key={`${c.ean}-${c.effective_date}`} className="p-3" data-testid={`change-${c.ean}`}>
              <div className="flex items-center gap-2 mb-1">
                {c.type === "new" ? (
                  <span className="chip bg-teal-light text-teal"><Sparkles className="w-3 h-3" /> New</span>
                ) : (
                  <span className="chip bg-coral-light text-coral">Changed</span>
                )}
                <span className="text-ink truncate flex-1">{c.description}</span>
              </div>
              <div className="text-xs text-muted font-mono mb-1">{c.ean} · {c.brand || "—"} · {c.effective_date}</div>
              <div className="text-[15px] font-medium">
                {c.type === "new" ? (
                  <span className="text-emerald">{money(c.new_price)}</span>
                ) : (
                  <span className="text-ink">{money(c.old_price)} <ArrowRight className="w-4 h-4 inline text-coral" /> <span className="text-coral">{money(c.new_price)}</span></span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {scan.observations?.length > 0 && (
        <>
          <h2 className="font-display text-lg text-ink mb-3">Matched observations</h2>
          <div className="card divide-y divide-stone-border" data-testid="scan-observations-list">
            {(scan.observations || []).map((o) => (
              <div key={`${o.ean}-${o.mauritius_price}`} className="flex items-center gap-3 p-3">
                <span className="flex-1 min-w-0">
                  <span className="block text-ink truncate">{o.description}</span>
                  <span className="block text-xs text-muted font-mono">{o.ean}</span>
                </span>
                <span className="text-ink font-medium">{money(o.mauritius_price)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
